import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, PhoneCall, ShieldCheck, X } from "lucide-react";
import { SITE } from "@/data/site";

const DISMISSED_KEY = "vin-quote-popup-dismissed";

export default function CallQuotePopup() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(DISMISSED_KEY) === "true";
    } catch {
      // The CTA can still display if storage is unavailable.
    }
    if (dismissed) return undefined;

    const timer = window.setTimeout(() => setVisible(true), 8000);
    return () => window.clearTimeout(timer);
  }, []);

  const close = () => {
    setVisible(false);
    try {
      window.sessionStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      // Closing should always work, even if storage is unavailable.
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          role="dialog"
          aria-modal="false"
          aria-label="Call for a VIN verification quote"
          data-testid="call-quote-popup"
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-50 left-3 right-3 bottom-[5.8rem] lg:left-auto lg:right-6 lg:bottom-6 lg:w-[430px] overflow-hidden rounded-[1.6rem] bg-gradient-to-br from-[#082d70] to-brand text-white shadow-[0_24px_70px_rgba(10,37,105,0.35)]"
        >
          <div className="absolute -right-16 -top-20 h-48 w-48 rounded-full bg-white/10" aria-hidden />
          <button
            type="button"
            onClick={close}
            aria-label="Close quote message"
            data-testid="close-call-quote-popup"
            className="absolute right-3 top-3 z-10 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 transition-colors"
          >
            <X size={20} />
          </button>

          <div className="relative p-6 sm:p-7 pr-14">
            <p className="flex items-center gap-2 text-sm font-semibold text-white/90">
              <PhoneCall size={17} /> Fast quote by phone
            </p>
            <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight mt-3">
              Need a VIN Verification Quote?
            </h2>
            <p className="text-white/85 text-sm sm:text-base leading-relaxed mt-3">
              Tell us your vehicle type and ZIP code. Get a fast quote for mobile service with no obligation.
            </p>
            <a
              href={SITE.phoneHref}
              data-testid="popup-call-for-quote"
              className="mt-5 min-h-14 rounded-full bg-accent text-white font-semibold flex items-center justify-center gap-2 px-6 shadow-[0_10px_28px_rgba(255,138,0,0.32)] hover:scale-[1.02] transition-transform"
            >
              <Phone size={19} /> Call Now for a Quote
            </a>
            <p className="mt-3 flex items-center justify-center gap-2 text-xs text-white/80">
              <ShieldCheck size={14} /> Licensed &amp; Bonded
            </p>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
